/**
 * RPC URL 管理工具
 *
 * 设计目标：
 * - 只用一个环境变量 REACT_APP_RPC_URL 配置 RPC，可放多个，用英文逗号分隔。
 *   例如：REACT_APP_RPC_URL=https://rpc-a.com,https://rpc-b.com
 * - 整个 app 始终使用「同一个」当前生效的 RPC（不再每次随机）。
 * - 页面打开时先探测当前 RPC，如果命中限流(429 / limit exceeded)或其他错误，
 *   就顺延切换到下一个，并让之后创建的所有 provider 都使用切换后的这个。
 * - 切换状态保存在内存中（不写 localStorage），每次刷新重新探测。
 */

const DEFAULT_RPC_URL = "https://bsc-dataseed.binance.org";

// 当前生效的 RPC 下标（内存态，刷新后重置为 0，由 ensureHealthyRpc 重新探测）
let activeIndex = 0;

/**
 * 解析环境变量，得到 RPC URL 列表（逗号或空白分隔）
 */
function getRpcList(): string[] {
  const raw = process.env.REACT_APP_RPC_URL || "";
  const list = raw
    .split(/[,\s]+/)
    .map(url => url.trim())
    .filter(Boolean);
  return list.length > 0 ? list : [DEFAULT_RPC_URL];
}

/**
 * 把下标限制在合法范围内
 */
function clampIndex(index: number, length: number): number {
  if (!Number.isFinite(index) || index < 0 || index >= length) return 0;
  return index;
}

/**
 * 获取当前生效的 RPC URL（同步，所有 provider 都应通过它取值）
 */
export function getActiveRpcUrl(): string {
  const list = getRpcList();
  return list[clampIndex(activeIndex, list.length)];
}

/**
 * 兼容旧调用名：返回当前生效的 RPC URL（不再随机）
 */
export function getRandomRpcUrl(): string {
  return getActiveRpcUrl();
}

/**
 * 兼容旧调用名：返回当前生效的 RPC URL
 */
export function getNewRandomRpcUrl(): string {
  return getActiveRpcUrl();
}

/**
 * 获取所有可用的 RPC URL
 */
export function getAllRpcUrls(): string[] {
  return getRpcList();
}

/**
 * 切换到下一个 RPC（运行时遇到错误时调用），返回切换后的 URL
 */
export function switchToNextRpc(): string {
  const list = getRpcList();
  activeIndex = (clampIndex(activeIndex, list.length) + 1) % list.length;
  const url = list[activeIndex];
  console.warn(`🔁 切换 RPC -> ${url} (${activeIndex + 1}/${list.length})`);
  return url;
}

/**
 * 判断一个错误是否为「RPC 限流 / RPC 错误」，命中则应切换节点
 */
export function isRpcLimitError(err: any): boolean {
  if (!err) return false;
  const status = err.status ?? err.response?.status;
  const code = err.code ?? err.error?.code;
  const message = (err.message || err.error?.message || err.reason || "").toString().toLowerCase();

  return (
    status === 429 ||
    code === 429 ||
    code === -32005 || // limit exceeded
    code === -32016 || // over rate limit（部分节点）
    message.includes("429") ||
    message.includes("too many requests") ||
    message.includes("rate limit") ||
    message.includes("limit exceeded") ||
    message.includes("exceeded the quota") ||
    message.includes("call rate limit")
  );
}

/**
 * 探测单个 RPC 是否健康（一次轻量 eth_chainId 调用，带超时）
 * @returns true 健康；false 不可用 / 限流 / 出错
 */
async function checkRpc(url: string, timeoutMs = 3000): Promise<boolean> {
  try {
    const controller = new AbortController();
    const timer = setTimeout(() => controller.abort(), timeoutMs);
    const res = await fetch(url, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ jsonrpc: "2.0", id: 1, method: "eth_chainId", params: [] }),
      signal: controller.signal,
    });
    clearTimeout(timer);

    if (res.status === 429 || !res.ok) return false; // 限流 / HTTP 错误
    const data = await res.json().catch(() => null);
    if (!data || data.error) return false; // RPC 层错误（含 limit exceeded）
    return Boolean(data.result);
  } catch {
    // 网络错误 / 超时 / abort
    return false;
  }
}

/**
 * 页面启动时调用：从当前节点开始依次探测，选出第一个健康的 RPC 并设为生效节点。
 * 应在创建任何 provider 之前 await，保证全 app 用同一个健康节点。
 * @returns 最终选定的 RPC URL
 */
export async function ensureHealthyRpc(): Promise<string> {
  const list = getRpcList();

  // 只有一个节点：无需探测，直接使用
  if (list.length <= 1) {
    activeIndex = 0;
    return list[0];
  }

  const start = clampIndex(activeIndex, list.length);
  for (let offset = 0; offset < list.length; offset++) {
    const idx = (start + offset) % list.length;
    const healthy = await checkRpc(list[idx]);
    if (healthy) {
      activeIndex = idx;
      if (offset > 0) console.warn(`🔁 启动探测：切换到健康 RPC -> ${list[idx]} (${idx + 1}/${list.length})`);
      return list[idx];
    }
    console.warn(`⚠️ RPC 不可用：${list[idx]} (${idx + 1}/${list.length})`);
  }

  // 全部探测失败：保持当前下标，让上层 provider 自行重试
  activeIndex = start;
  console.error("❌ 所有 RPC 探测均失败，使用默认下标继续");
  return list[start];
}

/**
 * 获取当前 RPC 配置状态（调试用）
 */
export function getRpcConfigInfo(): {
  availableUrls: string[];
  activeIndex: number;
  currentUrl: string;
} {
  const availableUrls = getRpcList();
  return {
    availableUrls,
    activeIndex: clampIndex(activeIndex, availableUrls.length),
    currentUrl: getActiveRpcUrl(),
  };
}
